import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams, Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import './scss/GlobalNavbar.scss';

// Icons
import Monitoring from '../icons/Monitoring';
import WandShine from '../icons/WandShine';
import Psychology from '../icons/Psychology';
import TextToSpeech from '../icons/TextToSpeech';
import Timeline from '../icons/Timeline';
import ChevronRight from '../icons/ChevronRight';
import Home from '../icons/Home';
import Settings from '../icons/Settings';
import Automation from '../icons/Automation';
import IconExtension from '../icons/Extension';
import Person from '../icons/Person';
import Workspaces from '../icons/Workspaces';
import Notifications from '../icons/Notifications';
import Payments from '../icons/Payments';
import ImportExport from '../icons/ImportExport';

const GlobalNavbar = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();
    const { user } = useSelector((state) => state.user);
    const { workspaces, guestWorkspaces } = useSelector((state) => state.workspaces);
    const [isChatbotOpen, setIsChatbotOpen] = useState(false);

    // Listen for chatbot state changes
    useEffect(() => {
        const handleChatbotStateChange = (event) => {
            setIsChatbotOpen(event.detail.isOpen);
        };

        window.addEventListener('chatbotStateChanged', handleChatbotStateChange);
        return () => {
            window.removeEventListener('chatbotStateChanged', handleChatbotStateChange);
        };
    }, []);

    // Don't show on board pages or auth pages
    const hiddenPaths = ['/login', '/signup', '/verify', '/recovery', '/reset', '/set-password'];
    const isBoardPage = location.pathname.startsWith('/b/');
    const isCardPage = location.pathname.startsWith('/c/');
    const isHiddenPage = hiddenPaths.some(path => location.pathname.startsWith(path));

    if (isCardPage || isBoardPage || isHiddenPage || !user) {
        return null;
    }

    // Helper function to get current workspace
    const getCurrentWorkspace = (workspaceId) => {
        // First check in regular workspaces
        const workspace = workspaces.find(ws => ws.shortId === workspaceId);
        if (workspace) return workspace;

        // Then check in guest workspaces
        const guestWorkspace = guestWorkspaces.find(ws => ws.shortId === workspaceId);
        return guestWorkspace || null;
    };

    // Generate breadcrumb based on current route
    const generateBreadcrumb = () => {
        const pathParts = location.pathname.split('/').filter(Boolean);
        const breadcrumbs = [];

        // Home breadcrumb
        // breadcrumbs.push({
        //     label: 'Home',
        //     path: '/',
        //     icon: <Home size={16} />
        // });

        // Route-specific breadcrumbs
        if (pathParts[0] === 'u' && pathParts[1]) {
            const username = pathParts[1];
            breadcrumbs.push({
                label: username,
                path: `/u/${username}/boards`,
                icon: <Person size={16} />
            });

            if (pathParts[2]) {
                const pageMap = {
                    'profile': 'Profile',
                    'boards': 'Boards',
                    'cards': 'Cards',
                    'activity': 'Activity',
                    'settings': 'Settings',
                    'white-label': 'White Label'
                };
                const pageName = pageMap[pathParts[2]] || pathParts[2];
                breadcrumbs.push({
                    label: pageName,
                    path: location.pathname
                });
            }
        } else if (pathParts[0] === 'w' && pathParts[1]) {
            const workspaceId = pathParts[1];
            const currentWorkspace = getCurrentWorkspace(workspaceId);
            const workspaceName = currentWorkspace?.name || 'Workspace';

            breadcrumbs.push({
                label: workspaceName,
                path: `/w/${workspaceId}/boards`,
                icon: <Workspaces size={16} />
            });

            if (pathParts[2]) {
                const pageMap = {
                    'boards': 'boards',
                    'members': 'members',
                    'settings': 'Settings'
                };
                const pageName = pageMap[pathParts[2]] || pathParts[2];
                breadcrumbs.push({
                    label: pageName,
                    path: location.pathname
                });
            }
        } else if (pathParts[0] === 'notifications') {
            breadcrumbs.push({
                label: 'Notifications',
                path: '/notifications',
                icon: <Notifications size={16} />
            });
        } else if (pathParts[0] === 'billing') {
            breadcrumbs.push({
                label: 'Billing',
                path: '/billing',
                icon: <Payments size={16} />
            });
        } else if (pathParts[0] === 'import') {
            breadcrumbs.push({
                label: 'Import',
                path: '/import',
                icon: <ImportExport size={16} />
            });
        }

        return breadcrumbs;
    };

    const handleAnalyticsClick = () => {
        // Navigate to analytics page - adjust based on current context
        if (params.boardId) {
            navigate(`/b/${params.boardId}/analytics`);
        } else {
            // Navigate to user analytics or general analytics
            navigate(`/u/${user?.user?.username}/analytics`);
        }
    };

    const handleAIInsightsClick = () => {
        // Toggle AI chatbot
        const chatbotEvent = new CustomEvent('toggleAIChatbot');
        window.dispatchEvent(chatbotEvent);
    };

    const handleAskAIClick = () => {
        // Open AI assistant with specific context
        const chatbotEvent = new CustomEvent('toggleAIChatbot', {
            detail: { mode: 'ask' }
        });
        window.dispatchEvent(chatbotEvent);
    };

    const handleTextToTasksClick = () => {
        // Open AI assistant in text-to-tasks mode
        const chatbotEvent = new CustomEvent('toggleAIChatbot', {
            detail: { mode: 'textToTasks' }
        });
        window.dispatchEvent(chatbotEvent);
    };

    const handleActivityClick = () => {
        navigate(`/u/${user?.user?.username}/activity`);
    };

    const breadcrumbs = generateBreadcrumb();

    return (
        <div className="global-navbar">
            {/* Left side - Breadcrumb */}
            <div className="global-navbar-left">
                <nav className="breadcrumb">
                    {breadcrumbs.map((crumb, index) => (
                        <React.Fragment key={index}>
                            {index > 0 && (
                                <span className="chevron-icon" style={{ color: 'var(--secondary-text-color)', fontSize: '14px' }}>{'/'}</span>
                            )}
                            <Link
                                to={crumb.path}
                                className={`breadcrumb-item ${index === breadcrumbs.length - 1 ? 'current' : ''}`}
                            >
                                {crumb.icon && <span className="breadcrumb-icon">{crumb.icon}</span>}
                                {crumb.label}
                            </Link>
                        </React.Fragment>
                    ))}
                </nav>
            </div>

            {/* Right side - Action Icons */}
            <div className="global-navbar-right">

                {/* <button
                    className={`navbar-action-btn ${isChatbotOpen ? 'active' : ''}`}
                    onClick={handleAIInsightsClick}
                    data-tooltip-content="Open AI Assistent"
                    data-tooltip-position="bottom"
                >
                    <WandShine size={18} />
                </button> */}

                {/* <button
                    className="navbar-action-btn"
                    onClick={handleActivityClick}
                    data-tooltip-content="Activity"
                    data-tooltip-position="bottom"
                >
                    <Timeline size={18} />
                </button>

                <button
                    className="navbar-action-btn"
                    onClick={() => navigate(`/u/${user?.user?.username}/settings`)}
                    data-tooltip-content="Settings"
                    data-tooltip-position="bottom"
                >
                    <Settings size={18} />
                </button> */}

                {/* Automation */}
                <button className="navbar-action-btn full-deactivated"
                    data-tooltip-content="Automation"
                    data-tooltip-position="bottom">
                    <Automation size={18} />
                </button>
                <button className="navbar-action-btn full-deactivated"
                    data-tooltip-content="Power Ups"
                    data-tooltip-position="bottom">
                    <IconExtension size={18} />
                </button>


                {/* Analytics
                AI Insights
                Ask AI
                Text to Tasks
                Settings
                Change background
                Custom Fields
                Automation
                Power-Ups
                Labels */}
            </div>
        </div>
    );
};

export default GlobalNavbar;
